import { observer } from 'mobx-react-lite';
import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { User, UserRole } from '../apiClients/workspace/types';
import TeamMemberCard from '../components/TeamMemberCard';
import { Card, CardContent, Input, PageLayout } from '../components/shared';
import { useLanguage } from '../hooks/shared';
import { useNavigationService, useUserStore } from '../stores';

const Team: React.FC = observer(() => {
  const params = useParams();
  const { workspaceSlug } = params;
  const userStore = useUserStore();
  const { t } = useLanguage();
  const navigationService = useNavigationService();

  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'role' | 'skillLevel'>('name');

  // Filter and sort users
  const filteredAndSortedUsers = useMemo(() => {
    // Return empty groups if users data is not loaded yet
    if (!userStore.hasUsersData) {
      return {
        adminUsers: [],
        regularUsers: [],
      };
    }

    let users = Array.from(userStore.users.values());

    // Filter by search query
    if (searchQuery) {
      users = users.filter((user) =>
        user.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Separate admins from regular users
    const adminUsers = users.filter((user) =>
      user.roles.includes(UserRole.Admin)
    );
    const regularUsers = users.filter(
      (user) => !user.roles.includes(UserRole.Admin)
    );

    // Sort function that puts current user first in each group
    const sortUsers = (a: User, b: User) => {
      // Current user always comes first in their group
      if (a.id === userStore.userId) return -1;
      if (b.id === userStore.userId) return 1;

      // Then sort by selected criteria
      if (sortBy === 'name') {
        return a.name.localeCompare(b.name);
      } else {
        // Sort by skill level (weight descending)
        const aWeight = userStore.getUserSkillLevelWeight(a.id);
        const bWeight = userStore.getUserSkillLevelWeight(b.id);
        return (bWeight ?? 0) - (aWeight ?? 0);
      }
    };

    // Sort each group
    adminUsers.sort(sortUsers);
    regularUsers.sort(sortUsers);

    return {
      adminUsers,
      regularUsers,
    };
  }, [searchQuery, sortBy, userStore.users.size, userStore.userId, userStore]);

  const handleMemberClick = (userId: string) => {
    navigationService.toProfile(workspaceSlug!, userId);
  };

  if (!workspaceSlug) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 pb-20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading workspace...</p>
        </div>
      </div>
    );
  }

  return (
    <PageLayout title={t('team.title')} icon="👥">
      <Card className="shadow-xl">
        <CardContent className="p-6 space-y-6">
          {/* Search and Filters */}
          <div className="p-4 bg-gray-50 rounded-lg space-y-4">
            <Input
              type="text"
              placeholder={t('team.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full"
            />

            <div className="flex gap-2">
              <button
                onClick={() => setSortBy('name')}
                className={`px-3 py-1 rounded text-sm ${
                  sortBy === 'name'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-200 text-gray-700'
                }`}
              >
                {t('team.sortByName')}
              </button>
              <button
                onClick={() => setSortBy('skillLevel')}
                className={`px-3 py-1 rounded text-sm ${
                  sortBy === 'skillLevel'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-200 text-gray-700'
                }`}
              >
                {t('team.sortBySkill')}
              </button>
            </div>
          </div>

          {/* Loading state */}
          {userStore.isLoadingUsers && (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">{t('common.loading')}</p>
            </div>
          )}

          {/* Error state */}
          {userStore.isUsersError && (
            <div className="p-6 text-center text-red-600">
              {t('errors.failedToLoadUsers')}
            </div>
          )}

          {/* Users content (only show when not loading and no error) */}
          {!userStore.isLoadingUsers && !userStore.isUsersError && (
            <div className="space-y-6">
              {/* Admins Section */}
              {filteredAndSortedUsers.adminUsers.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-gray-800 px-2 flex items-center">
                    <span className="text-xl mr-2">🦸</span>
                    {t('team.admins')}
                    <span className="bg-blue-100 text-blue-800 text-sm font-medium px-2.5 py-0.5 rounded-full ml-2">
                      {filteredAndSortedUsers.adminUsers.length}
                    </span>
                  </h3>
                  <div className="space-y-3">
                    {filteredAndSortedUsers.adminUsers.map((user) => (
                      <TeamMemberCard
                        key={user.id}
                        user={user}
                        onClick={() => handleMemberClick(user.id)}
                        isCurrentUser={user.id === userStore.userId}
                      />
                    ))}
                  </div>

                  {/* Separator between admins and regular users */}
                  {filteredAndSortedUsers.regularUsers.length > 0 && (
                    <div className="py-2">
                      <div className="border-t border-gray-200"></div>
                    </div>
                  )}
                </div>
              )}

              {/* Regular Users Section */}
              {filteredAndSortedUsers.regularUsers.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-gray-800 px-2 flex items-center">
                    <span className="text-xl mr-2">🧑‍💼</span>
                    {t('team.members')}
                    <span className="bg-blue-100 text-blue-800 text-sm font-medium px-2.5 py-0.5 rounded-full ml-2">
                      {filteredAndSortedUsers.regularUsers.length}
                    </span>
                  </h3>
                  <div className="space-y-3">
                    {filteredAndSortedUsers.regularUsers.map((user) => (
                      <TeamMemberCard
                        key={user.id}
                        user={user}
                        onClick={() => handleMemberClick(user.id)}
                        isCurrentUser={user.id === userStore.userId}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* No members message */}
              {filteredAndSortedUsers.adminUsers.length === 0 &&
                filteredAndSortedUsers.regularUsers.length === 0 && (
                  <div className="p-6 text-center text-gray-500">
                    <div className="text-gray-400 text-6xl mb-4">👥</div>
                    <p className="text-lg">{t('team.noMembers')}</p>
                  </div>
                )}
            </div>
          )}
        </CardContent>
      </Card>
    </PageLayout>
  );
});

export default Team;
